// layerPopup — W3 Classic レイヤー追加 (consult 2026-05-26) の pure helper.
//
// `packages/web` の vitest は **node env, no DOM** (vitest.config.ts:9). React
// component や PointerEvent を node 環境で素直に組み立てられないため、LayerPopup
// の「データモデル + 副作用のないロジック」をここに切り出して単体テスト可能に
// する (= floatingChatPopup.ts と同じ流儀). LayerPopup.tsx 側はこれらを組み合わ
// せて描画するだけ.
//
// 確定事項 (docs/_wip/w3-classic-layers.md §2.1-2.2 / §6):
// - レイヤーは **3 固定** (`CLASSIC_LAYER_COUNT` = 3, `@oekaki/protocol`).
//   内部 id 0/1/2. **並べ替えなし**.
// - popup は **上から id2 → id1 → id0** で表示する (= リスト上端が最前面、
//   Photoshop / CSP 慣習). この表示順は変えない.
// - ラベルは **作成順ネーミング** (Photoshop/CSP 準拠, §6): 表示番号 = id+1.
//   よって上から `レイヤー3`(id2/前) / `レイヤー2`(id1/中) / `レイヤー1`(id0/背).
//   表示番号は「作成順の安定した名前」であって位置ではない (将来 admin 並べ替え
//   時も名前は不変、§6). 内部 id 0/1/2 / stroke の layerId / engine 合成順は据え置き.
// - デフォルトアクティブは **最前面 (id2 = レイヤー3)**.
// - 操作は ①アクティブ選択 ②👁 表示/非表示 の 2 つだけ (per-layer clear 🗑 は
//   2026-05-26 に撤去 = 削除と混同 + 破壊的で頻度低. sendClear(layerId) の能力は
//   W5 admin 管理 UI で再利用するため温存、popup の露出だけ外した).

import { CLASSIC_LAYER_COUNT } from '@oekaki/protocol';

import type { SidebarDockSide } from './classicToolFloat.js';
import type { FloatingDragAnchor, FloatingPopupPosition } from './floatingChatPopup.js';

/**
 * 1 レイヤーの表示メタ. 内部 id は 0/1/2 据え置きで、`label` (レイヤーN) は表示専用
 * (docs/_wip/w3-classic-layers.md §6 確定: 内部 id 据え置き / 表示は作成順ネーミング).
 */
export interface ClassicLayerMeta {
  /** 内部 layer id (= stroke / clear に乗る値). 0/1/2、合成順は id 昇順 = 背→前. */
  readonly id: number;
  /** UI 表示名 (= `レイヤー${id+1}`). 作成順ネーミング (Photoshop/CSP 準拠, §6). */
  readonly label: string;
}

/**
 * 内部 id → 表示ラベル. **作成順ネーミング** (Photoshop/CSP 準拠, §6): 表示番号 =
 * id+1 で `レイヤー1`(id0/最背面) / `レイヤー2`(id1) / `レイヤー3`(id2/最前面).
 *
 * 表示番号は「作成順の安定した名前」であって位置ではない. 今は固定3層なので
 * id→ラベルは単純な (id+1) 写像でよい (将来 admin 並べ替え時も名前不変なのは
 * displayNumber を id とは別に持たせて吸収する想定、§6). 範囲外 / 非整数 id にも
 * defensive に `レイヤーN` を返して落とさない (本来は 0/1/2 のみ).
 */
export const classicLayerLabel = (id: number): string => `レイヤー${id + 1}`;

/**
 * popup に **上から表示する順** のレイヤーメタ配列を作る.
 *
 * リスト上端が最前面 (= id2) になるよう、id 降順 (`count-1 .. 0`) で並べる
 * (Photoshop / CSP layer palette 慣習). `count` は `CLASSIC_LAYER_COUNT` を
 * 既定にとる (= web ローカルに 3 をハードコードしない).
 *
 * 例 (count=3): `[{id:2,label:'レイヤー3'}, {id:1,label:'レイヤー2'}, {id:0,label:'レイヤー1'}]`.
 */
export const buildLayerRows = (
  count: number = CLASSIC_LAYER_COUNT,
): ReadonlyArray<ClassicLayerMeta> => {
  const rows: ClassicLayerMeta[] = [];
  for (let id = count - 1; id >= 0; id--) {
    rows.push({ id, label: classicLayerLabel(id) });
  }
  return rows;
};

/**
 * デフォルトアクティブ層 = **最前面** (= count-1, 3 層なら id2 = レイヤー3).
 *
 * docs/_wip/w3-classic-layers.md §2.2 / §6: 「デフォルトアクティブは最前面 (レイヤー3)」.
 */
export const defaultActiveLayerId = (count: number = CLASSIC_LAYER_COUNT): number =>
  Math.max(0, count - 1);

/**
 * 初期の全層表示状態 (= 全 visible). popup の local state の seed に使う.
 * id → visible の Record. 👁 トグルはこの map を更新し、engine.setLayerVisible に
 * 流す (= クライアントローカル、broadcast しない, §2.3).
 */
export const initialLayerVisibility = (
  count: number = CLASSIC_LAYER_COUNT,
): Record<number, boolean> => {
  const map: Record<number, boolean> = {};
  for (let id = 0; id < count; id++) map[id] = true;
  return map;
};

// ─── 位置 (ドラッグ移動) ─────────────────────────────────────────────────
//
// popup は出しっぱなし (閉じない) で、ヘッダを掴んでドラッグ移動だけできる.
// 移動の position 計算は floatingChatPopup.ts の `nextPositionFromDrag` /
// `FloatingDragAnchor` をそのまま流用する (= 同じ pure helper を共有). ここでは
// 「デフォルト位置 (右側の隅)」と「localStorage 永続化」だけを足す.

/** デフォルト位置を出す際の画面端マージン (px). */
export const LAYER_POPUP_MARGIN = 16;
/**
 * popup のおおよその幅 (= デフォルト位置算出用. 実寸は CSS が決める).
 * 各行は `[👁][ラベル レイヤーN][● 選択]` の 2 操作 (per-layer clear 🗑 を 2026-05-26
 * に撤去したぶん詰めた). LayerPopup.tsx の `w-[...]` と揃える.
 */
export const LAYER_POPUP_NOMINAL_WIDTH = 208;

/**
 * popup のデフォルト位置 = 画面右上寄り (描画の邪魔になりにくい隅).
 *
 * `innerWidth` / `innerHeight` を引数で受ける (= node test で window 非依存に
 * 検証できる). SSR / 計測不能時は左上 fallback (margin, margin).
 *
 * 2026-08-01 追加依頼: `dockSide` (= Classic サイドバーの現在のドック側、既定
 * `'left'`) が `'right'` のときは既定の右上だとサイドバーの常設グリップ帯
 * (`classicToolFloat.ts` §12 参照) と重なるため、左上へミラーする。**既に
 * localStorage に位置が保存されているケースはこの関数を呼ばない** (呼び出し側
 * `LayerPopup.tsx` が `readLayerPopupPosition` を先に見て、保存済みがあれば
 * そちらを優先する。= ユーザーが動かした位置を勝手に上書きしない)。
 */
export const defaultLayerPopupPosition = (
  innerWidth: number,
  innerHeight: number,
  dockSide: SidebarDockSide = 'left',
): FloatingPopupPosition => {
  if (!Number.isFinite(innerWidth) || !Number.isFinite(innerHeight)) {
    return { x: LAYER_POPUP_MARGIN, y: LAYER_POPUP_MARGIN };
  }
  if (dockSide === 'right') {
    return { x: LAYER_POPUP_MARGIN, y: LAYER_POPUP_MARGIN };
  }
  return {
    x: Math.max(LAYER_POPUP_MARGIN, innerWidth - LAYER_POPUP_NOMINAL_WIDTH - LAYER_POPUP_MARGIN),
    y: LAYER_POPUP_MARGIN,
  };
};

/** localStorage キー: popup 位置 (per-user 記憶, nice-to-have). */
export const LAYER_POPUP_POS_KEY = 'oekaki:classic-layer-popup-pos';

/** Minimal Storage shape (uiScalePreference.ts と同流儀の StorageLike 抽象). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * 保存済 position を読み出す. 不在 / 不正 JSON / storage 不可は `null`
 * (= caller がデフォルト位置に倒す). 例外は throw しない.
 */
export const readLayerPopupPosition = (storage: StorageLike): FloatingPopupPosition | null => {
  try {
    const raw = storage.getItem(LAYER_POPUP_POS_KEY);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as { x?: unknown }).x === 'number' &&
      typeof (parsed as { y?: unknown }).y === 'number'
    ) {
      const { x, y } = parsed as { x: number; y: number };
      if (Number.isFinite(x) && Number.isFinite(y)) return { x, y };
    }
    return null;
  } catch {
    return null;
  }
};

/** position を保存. storage 不可 / quota 超過は silently drop. */
export const writeLayerPopupPosition = (storage: StorageLike, pos: FloatingPopupPosition): void => {
  try {
    storage.setItem(LAYER_POPUP_POS_KEY, JSON.stringify({ x: pos.x, y: pos.y }));
  } catch {
    // 永続化できないだけで動作は継続させる.
  }
};

// re-export so LayerPopup.tsx imports drag types from one place.
export type { FloatingDragAnchor, FloatingPopupPosition };

// ─── LayerPopup 可視状態 (= 2026-05-31 layer-popup-visibility-toggle) ─────
//
// 経緯 (`docs/_wip/w3-classic-layers.md §2.2.1`): α 期の旧 Java 版ユーザから
// 「レイヤー窓を消したい / 旧式の手触りが欲しい」声を受け、LayerPopup の
// 可視状態を `ArgbSliderPanel` 内の `[レイヤー]` toggle で制御する設計に確定.
// state 自体はレイアウト lifecycle (= ClassicLayout) で持ち、per-user に
// localStorage 永続化する (= 既存 `oekaki:classic-layer-popup-pos` と同流儀).
//
// - **default**: true (= 初見ユーザに Layer 機能の存在を必ず認知させる).
// - **永続化**: 'true' / 'false' の string. 不正値・不在は default 扱い.
// - **storage 不可** (SSR / private browsing 等) は getters が default を返し、
//   setters は no-op (= 例外で UI が壊れない、`uiScalePreference.ts` と同流儀).

/** localStorage キー: LayerPopup の可視状態 (per-user 記憶). */
export const LAYER_POPUP_VISIBLE_KEY = 'oekaki:classic-layer-popup-visible';

/** LayerPopup 可視状態のデフォルト値. */
export const DEFAULT_LAYER_POPUP_VISIBLE = true;

/**
 * LayerPopup 可視状態の永続化 store. `createUiScalePreferenceStore` /
 * `createPaneSizePreferenceStore` と同型の lightweight wrapper.
 *
 * - `getInitial()`: lazy initializer 用. localStorage 値があれば true/false、無ければ default.
 * - `set(next)`: state 更新時に呼ぶ. localStorage 書き込み (storage 不可なら no-op).
 */
export interface LayerPopupVisibilityStore {
  getInitial(): boolean;
  set(next: boolean): void;
}

export interface CreateLayerPopupVisibilityStoreOptions {
  /**
   * 上書き用 storage. 既定は `globalThis.localStorage`. テストでは小さな Map ベースの
   * fake を渡す. `null` を渡すと no-op (= getters は常に default を返す).
   */
  readonly storage?: StorageLike | null;
}

const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = (globalThis as { localStorage?: StorageLike }).localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:layer-popup-visible__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export const createLayerPopupVisibilityStore = (
  opts: CreateLayerPopupVisibilityStoreOptions = {},
): LayerPopupVisibilityStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());
  return {
    getInitial(): boolean {
      if (storage === null) return DEFAULT_LAYER_POPUP_VISIBLE;
      try {
        const raw = storage.getItem(LAYER_POPUP_VISIBLE_KEY);
        if (raw === 'true') return true;
        if (raw === 'false') return false;
        return DEFAULT_LAYER_POPUP_VISIBLE;
      } catch {
        return DEFAULT_LAYER_POPUP_VISIBLE;
      }
    },
    set(next: boolean): void {
      if (storage === null) return;
      try {
        storage.setItem(LAYER_POPUP_VISIBLE_KEY, next ? 'true' : 'false');
      } catch {
        // 永続化できないだけで動作は継続させる.
      }
    },
  };
};
